/**
 * The sidebar's cloud/local split and the two runtime-keyed indexes built from
 * it, in one place.
 *
 * Extracted from `ActiveThreadsSection` when the three calls had to start
 * agreeing about where a durable `env_` Environment's runtime comes from.
 * Giving one of them the lookup sources and not the others is a real failure
 * mode rather than an untidiness: a project that reaches `cloudConnectedProjects`
 * but not `cloudConnectedProjectByRuntimeId` suppresses its own catalog row as
 * "already represented" and then renders nowhere.
 */
import { useMemo } from "react";

import type { ScopedProjectRef } from "@cmmd-forge/contracts";
import type { CloudProjectIdentity } from "./cloudProjectsGroup.logic";
import {
  mapCloudConnectedProjectsByRuntimeId,
  mapCloudInventoryProjectRefsByRuntimeId,
  splitSidebarProjectsByCloudEnvironment,
} from "./connectedCloudProjects.logic";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import type {
  CloudRuntimeCatalogRowRef,
  SavedEnvironmentRuntimeIndex,
} from "./sidebarThreadRuntimeIdentity";

export interface SidebarCloudProjectBucketsInput {
  readonly projects: readonly SidebarProjectSnapshot[];
  readonly projectIdentityByRuntimeId: ReadonlyMap<string, CloudProjectIdentity>;
  /** Saved Environments, whose durable records carry the runtime they connected. */
  readonly savedEnvironments: SavedEnvironmentRuntimeIndex;
  /** Catalog rows, whose `cmmdEnvironmentId` names the Environment they host. */
  readonly catalogRows: readonly CloudRuntimeCatalogRowRef[];
}

export interface SidebarCloudProjectBuckets {
  readonly cloudConnectedProjects: readonly SidebarProjectSnapshot[];
  readonly cloudInventoryProjects: readonly SidebarProjectSnapshot[];
  /** Everything no runtime answers for: desktop rows, and unresolvable cloud ones. */
  readonly runtimeInventoryProjects: readonly SidebarProjectSnapshot[];
  readonly cloudInventoryRefsByRuntimeId: ReadonlyMap<string, readonly ScopedProjectRef[]>;
  readonly cloudConnectedProjectByRuntimeId: ReadonlyMap<string, readonly SidebarProjectSnapshot[]>;
}

export function useSidebarCloudProjectBuckets(
  input: SidebarCloudProjectBucketsInput,
): SidebarCloudProjectBuckets {
  const runtimeSources = useMemo(
    () => ({ savedEnvironments: input.savedEnvironments, catalogRows: input.catalogRows }),
    [input.savedEnvironments, input.catalogRows],
  );
  const { cloudConnectedProjects, cloudInventoryProjects, localProjects } =
    splitSidebarProjectsByCloudEnvironment(
      input.projects,
      input.projectIdentityByRuntimeId,
      runtimeSources,
    );
  const cloudInventoryRefsByRuntimeId = useMemo(
    () => mapCloudInventoryProjectRefsByRuntimeId(cloudInventoryProjects, runtimeSources),
    [cloudInventoryProjects, runtimeSources],
  );
  const cloudConnectedProjectByRuntimeId = useMemo(
    () => mapCloudConnectedProjectsByRuntimeId(cloudConnectedProjects, runtimeSources),
    [cloudConnectedProjects, runtimeSources],
  );
  return {
    cloudConnectedProjects,
    cloudInventoryProjects,
    runtimeInventoryProjects: localProjects,
    cloudInventoryRefsByRuntimeId,
    cloudConnectedProjectByRuntimeId,
  };
}
